import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { ApiError } from '../../lib/api'
import {
  createIntegrator, disableIntegrator, listIntegrators, rotateIntegratorKey, updateIntegrator,
  type Integrator, type IntegratorCreated, type IntegratorRotated, type TsaConfigInput,
} from '../../lib/portal'

const API_BASE_HINT  = 'https://api.sign.wsoft.tr'
const SAMPLES_REPO   = 'https://github.com/volkansendag/wsign-integration-samples'

// Bir kez gösterilen gizli değerler — oluşturma ve rotate yanıtlarında ortak şekil.
type RevealedSecret = {
  title:      string
  keyPrefix:  string
  apiKey:     string
  hmacSecret: string
}

export default function DeveloperPage() {
  const [items, setItems]   = useState<Integrator[] | null>(null)
  const [error, setError]   = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [revealed, setRevealed]     = useState<RevealedSecret | null>(null)
  const [editing, setEditing]       = useState<Integrator | null>(null)
  const [confirm, setConfirm]       = useState<
    | { kind: 'rotate' | 'disable'; item: Integrator }
    | null
  >(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const reload = () => {
    listIntegrators()
      .then(setItems)
      .catch(err => {
        setItems([])
        setError(err instanceof ApiError ? err.message : 'API anahtarları yüklenemedi.')
      })
  }
  useEffect(reload, [])

  const onCreated = (created: IntegratorCreated) => {
    setShowCreate(false)
    setRevealed({
      title:      created.name,
      keyPrefix:  created.keyPrefix,
      apiKey:     created.apiKey,
      hmacSecret: created.hmacSecret,
    })
    reload()
  }

  const doRotate = async (item: Integrator) => {
    setConfirm(null)
    setBusyId(item.integratorId)
    setError(null)
    try {
      const r: IntegratorRotated = await rotateIntegratorKey(item.integratorId)
      setRevealed({ title: item.name, keyPrefix: r.keyPrefix, apiKey: r.apiKey, hmacSecret: r.hmacSecret })
      reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Anahtar yenilenemedi.')
    } finally {
      setBusyId(null)
    }
  }

  const doDisable = async (item: Integrator) => {
    setConfirm(null)
    setBusyId(item.integratorId)
    setError(null)
    try {
      await disableIntegrator(item.integratorId)
      reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Anahtar devre dışı bırakılamadı.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <Helmet><title>Geliştirici — W.Sign</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <section className="container-page py-12">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold">Geliştirici</h1>
            <p className="mt-1 max-w-2xl text-sm text-[var(--color-fg-muted)]">
              Uygulamanızı W.Sign'a bağlamak için API anahtarları oluşturun ve izin verilen
              alan adlarını yönetin. Her anahtarın gizli değerleri yalnızca bir kez gösterilir.
            </p>
          </div>
          {items && items.length > 0 && (
            <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
              Yeni API anahtarı
            </button>
          )}
        </div>

        {error && (
          <p className="mt-6 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {/* ── Quickstart ─────────────────────────────────────────────── */}
        <Quickstart />

        {/* ── List / empty / loading ─────────────────────────────────── */}
        <h2 className="mt-10 text-xl font-semibold">API anahtarlarınız</h2>
        {items === null ? (
          <p className="mt-4 text-sm text-[var(--color-fg-muted)]">Yükleniyor…</p>
        ) : items.length === 0 ? (
          <div className="card mt-4 text-center">
            <p className="text-sm text-[var(--color-fg-muted)]">
              Henüz API anahtarınız yok. İlk anahtarınızı oluşturarak entegrasyona başlayın.
            </p>
            <button type="button" className="btn-primary mt-4" onClick={() => setShowCreate(true)}>
              İlk anahtarı oluştur
            </button>
          </div>
        ) : (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {items.map(item => (
              <IntegratorCard
                key={item.integratorId}
                item={item}
                busy={busyId === item.integratorId}
                onEdit={() => setEditing(item)}
                onRotate={() => setConfirm({ kind: 'rotate', item })}
                onDisable={() => setConfirm({ kind: 'disable', item })}
              />
            ))}
          </div>
        )}
      </section>

      {showCreate && (
        <CreateKeyModal onClose={() => setShowCreate(false)} onCreated={onCreated} />
      )}
      {revealed && (
        <SecretModal secret={revealed} onClose={() => setRevealed(null)} />
      )}
      {editing && (
        <EditAllowlistModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}
      {confirm && (
        <ConfirmDialog
          confirm={confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={() => confirm.kind === 'rotate' ? doRotate(confirm.item) : doDisable(confirm.item)}
        />
      )}
    </>
  )
}

// ── Integrator card ────────────────────────────────────────────────────────────

function IntegratorCard({
  item, busy, onEdit, onRotate, onDisable,
}: {
  item:      Integrator
  busy:      boolean
  onEdit:    () => void
  onRotate:  () => void
  onDisable: () => void
}) {
  const quota = item.monthlyQuota || 0
  const usage = item.monthlyUsage || 0
  const pct   = quota > 0 ? Math.min(100, Math.round((usage / quota) * 100)) : 0
  const near  = pct >= 90

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold">{item.name}</h3>
          <code className="mt-0.5 block text-xs text-[var(--color-fg-dim)]">{item.keyPrefix}…</code>
        </div>
        <span className={item.enabled ? 'badge badge-ok' : 'badge'}>
          {item.enabled ? 'Etkin' : 'Devre dışı'}
        </span>
      </div>

      {/* Kota */}
      <div className="mt-4">
        <div className="flex items-baseline justify-between text-xs text-[var(--color-fg-muted)]">
          <span>Aylık kota kullanımı</span>
          <span className="font-mono">
            {usage.toLocaleString('tr-TR')} / {quota > 0 ? quota.toLocaleString('tr-TR') : '∞'}
          </span>
        </div>
        <div
          className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[var(--color-bg-soft)]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={quota || undefined}
          aria-valuenow={usage}
          aria-label={`${item.name} aylık kota kullanımı`}
        >
          <div
            className={`h-full rounded-full ${near ? 'bg-[var(--color-warn)]' : 'bg-[var(--color-accent)]'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Allowlist'ler */}
      <HostList label="Callback alan adları" hosts={item.callbackDomainAllow} emptyText="Webhook yok" />
      <HostList label="Yönlendirme alan adları" hosts={item.redirectDomainAllow} />

      {/* Zaman damgası durumu */}
      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">Zaman damgası</p>
        {item.tsaConfigured ? (
          <p className="mt-1 text-xs text-[var(--color-fg)]">
            <span className="badge badge-ok">Kamu SM (yapılandırıldı)</span>
            {item.tsaHashAlgorithm && (
              <span className="ml-1.5 font-mono text-[11px] text-[var(--color-fg-dim)]">{item.tsaHashAlgorithm}</span>
            )}
          </p>
        ) : (
          <p className="mt-1 text-xs text-[var(--color-fg-dim)]">Yok — imzalar damgasız (CAdES-B)</p>
        )}
      </div>

      <div className="mt-5 flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-4">
        <button type="button" className="btn-ghost text-xs" onClick={onEdit} disabled={busy}>
          Alan adlarını düzenle
        </button>
        <button type="button" className="btn-ghost text-xs" onClick={onRotate} disabled={busy}>
          Anahtarı yenile
        </button>
        {item.enabled && (
          <button
            type="button"
            className="btn-ghost text-xs text-red-700 hover:bg-red-50"
            onClick={onDisable}
            disabled={busy}
          >
            Devre dışı bırak
          </button>
        )}
      </div>
    </div>
  )
}

function HostList({ label, hosts, emptyText = 'Tanımlı değil' }: { label: string; hosts: string[]; emptyText?: string }) {
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">{label}</p>
      {hosts.length === 0 ? (
        <p className="mt-1 text-xs text-[var(--color-fg-dim)]">{emptyText}</p>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {hosts.map(h => (
            <span key={h} className="badge font-mono text-[11px]">{h}</span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Quickstart help box ──────────────────────────────────────────────────────────

function Quickstart() {
  return (
    <div className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-5">
      <h2 className="text-sm font-semibold">Hızlı başlangıç</h2>
      <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
        Oluşturduğunuz değerleri örnek uygulamanın <code>.env</code> dosyasına ekleyin:
      </p>
      <pre className="mt-3 overflow-x-auto rounded-md border border-[var(--color-border)] bg-white px-4 py-3 text-xs leading-relaxed">
{`WSIGN_API_KEY=wsk_...
WSIGN_CALLBACK_SECRET=...
WSIGN_API_BASE=${API_BASE_HINT}`}
      </pre>
      <p className="mt-3 text-sm text-[var(--color-fg-muted)]">
        Örnek uygulamalar ve entegrasyon rehberi:{' '}
        <a
          href={SAMPLES_REPO}
          target="_blank"
          rel="noreferrer"
          className="text-[var(--color-accent)] hover:underline"
        >
          entegrasyon örnekleri deposu
        </a>
      </p>
      <p className="mt-3 text-xs text-[var(--color-fg-dim)]">
        Not: Geliştirme için <code>localhost</code> ekleyebilirsiniz; ancak sunucudan gönderilen
        callback isteği <code>localhost</code> adresinize ulaşamaz. Gerçek callback testi için
        herkese açık bir URL veya tünel (ör. ngrok) kullanın.
      </p>
    </div>
  )
}

// ── Create key modal ─────────────────────────────────────────────────────────────

function CreateKeyModal({
  onClose, onCreated,
}: {
  onClose:   () => void
  onCreated: (c: IntegratorCreated) => void
}) {
  const [name, setName]         = useState('')
  const [callbacks, setCallbacks] = useState<string[]>([])
  const [redirects, setRedirects] = useState<string[]>([])
  const [tsa, setTsa]     = useState<TsaFields>(emptyTsaFields)
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!name.trim()) { setError('Bir ad girin.'); return }
    if (redirects.length === 0) { setError('En az bir yönlendirme alan adı ekleyin.'); return }
    const tsaResult = buildTsaPayload('create', false, tsa)
    if ('error' in tsaResult) { setError(tsaResult.error); return }
    setBusy(true); setError(null)
    try {
      const created = await createIntegrator({
        name:                name.trim(),
        callbackDomainAllow: callbacks,   // boş bırakılabilir — webhook opsiyonel
        redirectDomainAllow: redirects,
        ...tsaResult.payload,
      })
      onCreated(created)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'API anahtarı oluşturulamadı.')
      setBusy(false)
    }
  }

  return (
    <Modal title="Yeni API anahtarı" onClose={busy ? undefined : onClose}>
      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">Ad</span>
        <input
          type="text" value={name} placeholder="Örn. Üretim ortamı"
          autoFocus
          onChange={e => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
        />
      </label>

      <ChipInput
        label="Callback alan adları (opsiyonel)"
        hint="Webhook (sunucu→siz POST) kullanmıyorsanız boş bırakın. redirect / pull / post (kapalı sistem) için callback gerekmez. Webhook için public host ekleyin (localhost olmaz)."
        chips={callbacks}
        onChange={setCallbacks}
      />
      <ChipInput
        label="Yönlendirme alan adları"
        hint="localhost eklenebilir (tarayıcı yönlendirmesi/post teslimi için)."
        chips={redirects}
        onChange={setRedirects}
      />

      <TsaConfigSection mode="create" tsaConfigured={false} tsa={tsa} onChange={setTsa} />

      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

      <div className="mt-6 flex justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>İptal</button>
        <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Oluşturuluyor…' : 'Oluştur'}
        </button>
      </div>
    </Modal>
  )
}

// ── Edit allowlist modal ─────────────────────────────────────────────────────────

function EditAllowlistModal({
  item, onClose, onSaved,
}: {
  item:    Integrator
  onClose: () => void
  onSaved: () => void
}) {
  const [callbacks, setCallbacks] = useState<string[]>(item.callbackDomainAllow)
  const [redirects, setRedirects] = useState<string[]>(item.redirectDomainAllow)
  const [tsa, setTsa]     = useState<TsaFields>(() => ({
    url:           item.tsaUrl ?? '',
    username:      item.tsaUsername ?? '',
    password:      '',                                  // write-only — mevcut parola hiç gösterilmez
    policyOid:     item.tsaPolicyOid ?? '',
    hashAlgorithm: item.tsaHashAlgorithm || 'SHA256',
  }))
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (redirects.length === 0) { setError('En az bir yönlendirme alan adı ekleyin.'); return }
    const tsaResult = buildTsaPayload('edit', item.tsaConfigured, tsa)
    if ('error' in tsaResult) { setError(tsaResult.error); return }
    setBusy(true); setError(null)
    try {
      await updateIntegrator(item.integratorId, {
        callbackDomainAllow: callbacks,   // boş bırakılabilir — webhook opsiyonel
        redirectDomainAllow: redirects,
        ...tsaResult.payload,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Alan adları güncellenemedi.')
      setBusy(false)
    }
  }

  return (
    <Modal title={`Alan adları — ${item.name}`} onClose={busy ? undefined : onClose}>
      <ChipInput
        label="Callback alan adları (opsiyonel)"
        hint="Webhook (sunucu→siz POST) kullanmıyorsanız boş bırakın. redirect / pull / post (kapalı sistem) için callback gerekmez. Webhook için public host ekleyin (localhost olmaz)."
        chips={callbacks}
        onChange={setCallbacks}
      />
      <ChipInput
        label="Yönlendirme alan adları"
        hint="localhost eklenebilir (tarayıcı yönlendirmesi/post teslimi için)."
        chips={redirects}
        onChange={setRedirects}
      />

      <TsaConfigSection mode="edit" tsaConfigured={item.tsaConfigured} tsa={tsa} onChange={setTsa} />

      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

      <div className="mt-6 flex justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>İptal</button>
        <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </div>
    </Modal>
  )
}

// ── One-time secret reveal modal ─────────────────────────────────────────────────

function SecretModal({ secret, onClose }: { secret: RevealedSecret; onClose: () => void }) {
  const [ack, setAck] = useState(false)
  return (
    <Modal title="API anahtarı oluşturuldu" onClose={undefined}>
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        <strong>Bu değerler bir daha gösterilmeyecek.</strong> Şimdi kopyalayıp güvenli bir
        yerde saklayın. Kaybederseniz anahtarı yenilemeniz gerekir.
      </div>

      <p className="mt-4 text-xs text-[var(--color-fg-muted)]">
        Anahtar: <span className="font-medium text-[var(--color-fg)]">{secret.title}</span>{' '}
        <code className="text-[var(--color-fg-dim)]">({secret.keyPrefix}…)</code>
      </p>

      <SecretField label="API anahtarı (WSIGN_API_KEY)" value={secret.apiKey} />
      <SecretField label="Callback secret (WSIGN_CALLBACK_SECRET)" value={secret.hmacSecret} />

      <label className="mt-5 flex items-start gap-2 text-sm">
        <input
          type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)]"
        />
        <span>Bu değerleri güvenli bir yere kaydettim.</span>
      </label>

      <div className="mt-5 flex justify-end">
        <button type="button" className="btn-primary" onClick={onClose} disabled={!ack}>
          Kapat
        </button>
      </div>
    </Modal>
  )
}

// ── Confirm dialog ───────────────────────────────────────────────────────────────

function ConfirmDialog({
  confirm, onCancel, onConfirm,
}: {
  confirm:   { kind: 'rotate' | 'disable'; item: Integrator }
  onCancel:  () => void
  onConfirm: () => void
}) {
  const rotate = confirm.kind === 'rotate'
  return (
    <Modal title={rotate ? 'Anahtarı yenile' : 'Anahtarı devre dışı bırak'} onClose={onCancel}>
      <p className="text-sm text-[var(--color-fg-muted)]">
        {rotate ? (
          <>
            <span className="font-medium text-[var(--color-fg)]">{confirm.item.name}</span> için yeni
            bir API anahtarı ve callback secret üretilecek. <strong>Eski anahtar derhal geçersiz
            olur</strong> ve onu kullanan entegrasyonlar çalışmayı durdurur. Devam edilsin mi?
          </>
        ) : (
          <>
            <span className="font-medium text-[var(--color-fg)]">{confirm.item.name}</span> devre dışı
            bırakılacak ve anahtarı kabul edilmeyecek. Bu işlem entegrasyonu durdurur. Devam edilsin mi?
          </>
        )}
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onCancel}>Vazgeç</button>
        <button
          type="button"
          className={rotate ? 'btn-primary' : 'btn-primary !bg-red-600 hover:!bg-red-700'}
          onClick={onConfirm}
        >
          {rotate ? 'Yenile' : 'Devre dışı bırak'}
        </button>
      </div>
    </Modal>
  )
}

// ── TSA (RFC 3161 zaman damgası) yapılandırması ───────────────────────────────────

const TSA_HASH_ALGORITHMS = ['SHA256', 'SHA384', 'SHA512'] as const

/** Modal-içi düzenlenebilir TSA alanları (parola dahil — yalnız form state'inde). */
type TsaFields = {
  url:           string
  username:      string
  password:      string
  policyOid:     string
  hashAlgorithm: string
}

const emptyTsaFields: TsaFields = { url: '', username: '', password: '', policyOid: '', hashAlgorithm: 'SHA256' }

/**
 * Form alanlarından gönderilecek TSA payload'ını üretir veya doğrulama hatası döner.
 * - TSA hiç istenmediyse (tüm alanlar boş ve edit'te yapılandırılmamış) → boş payload (opsiyonel).
 * - TSA isteniyorsa URL + kullanıcı adı zorunlu.
 * - Parola: create'te veya henüz yapılandırılmamış entegratörde zorunlu;
 *   zaten yapılandırılmış entegratörü düzenlerken boş bırakılabilir (mevcut parola korunur).
 */
function buildTsaPayload(
  mode: 'create' | 'edit',
  tsaConfigured: boolean,
  fields: TsaFields,
): { payload: TsaConfigInput } | { error: string } {
  const url       = fields.url.trim()
  const username  = fields.username.trim()
  const password  = fields.password          // parolayı trim'leme — birebir gönder
  const policyOid = fields.policyOid.trim()

  const anyFilled = !!(url || username || password || policyOid)

  // Yapılandırılmış entegratörde tüm TSA alanları boşaltıldıysa → zaman damgasını KALDIR.
  // Backend, boş (ama mevcut) tsaUrl'i "devre dışı bırak" olarak yorumlar → URL/kullanıcı/parola
  // hepsi temizlenir (CAdES-T kapanır). Bu, paneldan damgayı kaldırmanın tek yolu.
  if (mode === 'edit' && tsaConfigured && !anyFilled) {
    return { payload: { tsaUrl: '' } }
  }

  if (!anyFilled) return { payload: {} }   // TSA opsiyonel — boşsa hiçbir şey gönderme

  if (!url || !username) {
    return { error: 'Zaman damgası için TSA URL ve kullanıcı adı birlikte gereklidir.' }
  }
  const passwordRequired = mode === 'create' || !tsaConfigured
  if (passwordRequired && !password) {
    return { error: 'Zaman damgası için TSA parolası gereklidir.' }
  }

  const payload: TsaConfigInput = {
    tsaUrl:           url,
    tsaUsername:      username,
    tsaHashAlgorithm: fields.hashAlgorithm || 'SHA256',
  }
  if (password)  payload.tsaPassword  = password   // boşsa gönderme → mevcut parola korunur
  if (policyOid) payload.tsaPolicyOid = policyOid
  return { payload }
}

/** Create/Edit modallarında ortak, katlanır TSA yapılandırma bölümü. */
function TsaConfigSection({
  mode, tsaConfigured, tsa, onChange,
}: {
  mode:          'create' | 'edit'
  tsaConfigured: boolean
  tsa:           TsaFields
  onChange:      (next: TsaFields) => void
}) {
  const hasValues = !!(tsa.url || tsa.username || tsa.password || tsa.policyOid)
  const [open, setOpen] = useState(() => (mode === 'edit' && tsaConfigured) || hasValues)
  const set = (patch: Partial<TsaFields>) => onChange({ ...tsa, ...patch })

  const inputClass =
    'mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none'

  return (
    <div className="mt-5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)]">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold">
          Zaman damgası (Kamu SM / RFC 3161){' '}
          <span className="font-normal text-[var(--color-fg-dim)]">— opsiyonel</span>
        </span>
        <span className="text-[var(--color-fg-dim)]">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="border-t border-[var(--color-border)] px-4 py-4">
          <p className="text-xs leading-relaxed text-[var(--color-fg-muted)]">
            Kendi Kamu SM TSA hesabınızı girin; imzalarınız zaman damgalı (CAdES-T) üretilir.
            Boş bırakılırsa imzalar damgasız (CAdES-B) olur. Kimlik bilgileriniz sunucuda şifreli
            saklanır, asla geri gösterilmez.
          </p>

          <label className="mt-4 block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">TSA URL</span>
            <input
              type="url"
              value={tsa.url}
              placeholder="Kamu SM zaman damgası endpoint'i, ör. http://zd.kamusm.gov.tr"
              onChange={e => set({ url: e.target.value })}
              className={inputClass}
            />
          </label>

          <label className="mt-4 block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">TSA kullanıcı adı</span>
            <input
              type="text"
              value={tsa.username}
              autoComplete="off"
              onChange={e => set({ username: e.target.value })}
              className={inputClass}
            />
          </label>

          <label className="mt-4 block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">TSA parolası</span>
            <input
              type="password"
              value={tsa.password}
              autoComplete="new-password"
              placeholder={mode === 'edit' && tsaConfigured ? '••••••••  (değiştirmek için yazın)' : ''}
              onChange={e => set({ password: e.target.value })}
              className={inputClass}
            />
            {mode === 'edit' && tsaConfigured && (
              <p className="mt-1 text-xs text-[var(--color-fg-dim)]">Boş bırakılırsa mevcut parola korunur.</p>
            )}
          </label>

          <label className="mt-4 block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">Policy OID (opsiyonel)</span>
            <input
              type="text"
              value={tsa.policyOid}
              placeholder="ör. 2.16.792.3.0.1.1"
              onChange={e => set({ policyOid: e.target.value })}
              className={inputClass}
            />
          </label>

          <label className="mt-4 block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">Hash algoritması (opsiyonel)</span>
            <select
              value={tsa.hashAlgorithm || 'SHA256'}
              onChange={e => set({ hashAlgorithm: e.target.value })}
              className={inputClass}
            >
              {TSA_HASH_ALGORITHMS.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>

          {mode === 'edit' && tsaConfigured && (
            <div className="mt-5 border-t border-[var(--color-border)] pt-4">
              <button
                type="button"
                onClick={() => onChange(emptyTsaFields)}
                className="text-sm font-medium text-[var(--color-accent)] hover:underline"
              >
                Zaman damgasını kaldır
              </button>
              <p className="mt-1 text-xs text-[var(--color-fg-dim)]">
                Tüm TSA alanlarını temizler; <strong>Kaydet</strong>'e bastığınızda zaman damgası
                tümüyle silinir (imzalar damgasız / CAdES-B olur).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Reusable building blocks ─────────────────────────────────────────────────────

/** Maskelenmiş gizli değer + göster/gizle + kopyala. */
function SecretField({ label, value }: { label: string; value: string }) {
  const [shown, setShown]    = useState(false)
  const [copied, setCopied]  = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Pano erişimi reddedildi — kullanıcı değeri gösterip elle kopyalayabilir.
      setShown(true)
    }
  }

  return (
    <div className="mt-4">
      <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">{label}</span>
      <div className="mt-1 flex items-stretch gap-2">
        <code className="flex-1 truncate rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-3 py-2 text-xs">
          {shown ? value : '•'.repeat(Math.min(40, value.length))}
        </code>
        <button
          type="button"
          className="btn-ghost px-3 text-xs"
          onClick={() => setShown(s => !s)}
          aria-label={shown ? 'Gizle' : 'Göster'}
        >
          {shown ? 'Gizle' : 'Göster'}
        </button>
        <button type="button" className="btn-primary px-3 text-xs" onClick={copy}>
          {copied ? 'Kopyalandı' : 'Kopyala'}
        </button>
      </div>
    </div>
  )
}

/** Host chip girişi — Enter veya virgül ile ekle, çarpı ile çıkar. */
function ChipInput({
  label, hint, chips, onChange,
}: {
  label:    string
  hint?:    string
  chips:    string[]
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')

  const add = (raw: string) => {
    const host = raw.trim().toLowerCase().replace(/,$/, '')
    if (!host) return
    if (!chips.includes(host)) onChange([...chips, host])
    setDraft('')
  }
  const remove = (host: string) => onChange(chips.filter(h => h !== host))

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      add(draft)
    } else if (e.key === 'Backspace' && !draft && chips.length > 0) {
      remove(chips[chips.length - 1])
    }
  }

  return (
    <div className="mt-4">
      <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">{label}</span>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-white px-2 py-2 focus-within:border-[var(--color-accent)]">
        {chips.map(h => (
          <span key={h} className="badge font-mono text-[11px]">
            {h}
            <button
              type="button"
              className="ml-0.5 text-[var(--color-fg-dim)] hover:text-red-600"
              onClick={() => remove(h)}
              aria-label={`${h} kaldır`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          placeholder={chips.length === 0 ? 'host ekleyin, Enter ile onaylayın' : ''}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => add(draft)}
          className="min-w-[8rem] flex-1 bg-transparent px-1 py-0.5 text-sm focus:outline-none"
        />
      </div>
      {hint && <p className="mt-1 text-xs text-[var(--color-fg-dim)]">{hint}</p>}
    </div>
  )
}

/** Basit merkez-modal. onClose verilmezse arkaplana tıklama kapatmaz (zorunlu akış). */
function Modal({
  title, children, onClose,
}: {
  title:     string
  children:  React.ReactNode
  onClose?:  () => void
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose ? () => onClose() : undefined}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          {onClose && (
            <button
              type="button"
              className="-mr-1 rounded p-1 text-[var(--color-fg-dim)] hover:bg-[var(--color-bg-soft)] hover:text-[var(--color-fg)]"
              onClick={onClose}
              aria-label="Kapat"
            >
              ×
            </button>
          )}
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}
